import React, { FC, useEffect } from "react";

import { DataViewKey } from "@keepeek/commons";
import {
  BaseComponentProps,
  KButton,
  ListDataView,
  SanitizeHtml,
  useThemeColors,
  withSuspense,
} from "@keepeek/refront-components";
import { Box } from "@mui/material";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";

import { KIcon } from "../../../components/common/KIcon";
import logger from "../../../lib/logger-utils";
import { AssociationsWidget as AssociationsWidgetModel } from "../../../models/configuration/components/associationsWidget";
import { useBackContext } from "../../../providers/backContext/hooks";
import EditableSanitizeHtml from "../../FrontEdit/EditableSanitizeHtml";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetProps } from "../models";
import { parseOptionsInternalUrl } from "../utils/parseInternalUrl";
import { useAssociationsWidget } from "./hooks";
import StyledAssociationWidget from "./style";

export type AssociationsWidgetProps = {
  widgetId: string;
} & Omit<AssociationsWidgetModel, "id"> &
  BaseComponentProps &
  KWidgetProps;

const AssociationsWidget: FC<React.PropsWithChildren<AssociationsWidgetProps>> = function ({
  testId = "AssociationsWidget",
  className,
  backgroundColor,
  id,
  elementId: elementIdWidget,
  mediaLinkIds,
  widgetId,
  hideDownloadAll,
  title,
  type,
}) {
  const { t } = useTranslation("refront-components");
  const { setWidgetState, widgetState } = useWidgetState({
    id: widgetId,
    type,
  });
  const { locale } = useRouter();
  const { elementId, downloadAll, elements, filters, columns } = useAssociationsWidget({
    elementIdWidget,
    mediaLinkIds,
    dataViewKey: DataViewKey.ASSOCIATION,
  });

  const titleLocale = title?.find((t) => t.language === locale)?.value ?? "";

  const { convertColor } = useThemeColors();
  const { goToPageWithBackContext } = useBackContext();

  useEffect(() => {
    if (!elements) {
      logger.debug(`AssociationsWidget id "${widgetId}" does not have any element to show`);
      setWidgetState({ state: WidgetUIState.NULL });
    } else if (!elementId) {
      logger.error(
        "AssociationsWidget elementId not given in configuration and we're not in a element detail page either",
      );
      setWidgetState({ state: WidgetUIState.NULL });
    } else if (elements && elements.length > 0) {
      setWidgetState({ state: WidgetUIState.RENDERED, elementCount: elements.length });
    } else {
      setWidgetState({ state: WidgetUIState.NULL });
    }
  }, [elements, elementId, widgetId, setWidgetState]);

  switch (widgetState.state) {
    case WidgetUIState.RENDERED:
      return (
        <StyledAssociationWidget
          id={id}
          data-testid={testId}
          className={clsx("AssociationsWidget", `AssociationsWidget-${widgetId}`, className)}
          style={{ backgroundColor: convertColor(backgroundColor), flex: 1 }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-end"
            padding=".5rem"
            style={{ gap: "1rem" }}
            className="AssociationsWidget-title"
          >
            {(elements?.length ?? 0) > 0 && (
              <SanitizeHtml parseOptions={parseOptionsInternalUrl} html={titleLocale} />
            )}
            {(elements?.length ?? 0) > 0 && hideDownloadAll !== true && (
              <KButton variant="text" size="small" onClick={downloadAll}>
                {/* iso dataView */}
                <EditableSanitizeHtml
                  labelKey={"dataview.action.download.label"}
                  labelOptions={{ count: elements?.length ?? 0 }}
                />
              </KButton>
            )}
          </Box>
          <ListDataView
            dataViewKey={DataViewKey.ASSOCIATION}
            KIcon={KIcon}
            shareTokenDownloadAuthorized={false}
            goToPageWithBackContext={goToPageWithBackContext}
            filters={filters}
            columns={columns}
            title={t("components.widget.associations.table.title")}
            summary={t("components.widget.associations.table.summary")}
          />
        </StyledAssociationWidget>
      );
    default:
      return null;
  }
};
export default withSuspense(AssociationsWidget);
